import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react';
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;
const clampZoom = (value: number) =>
  Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

export const useImageViewport = (
  activeKey: string,
  isImagePreview: boolean
) => {
  const imageViewportRef = useRef<HTMLDivElement | null>(null);
  const zoomRef = useRef(1);
  const sourceKeyRef = useRef(activeKey);
  sourceKeyRef.current = activeKey;
  const initialImageZoomRef = useRef(1);
  const [imageViewportElement, setImageViewportElement] =
    useState<HTMLDivElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const adjustZoom = useCallback((delta: number) => {
    const currentZoom = zoomRef.current;
    const nextZoom = clampZoom(currentZoom + delta);
    if (nextZoom === currentZoom) return;
    zoomRef.current = nextZoom;
    setZoom(nextZoom);
  }, []);

  const resetZoom = useCallback(() => {
    const viewport = imageViewportRef.current;
    const nextZoom = isImagePreview ? initialImageZoomRef.current : 1;
    zoomRef.current = nextZoom;
    setZoom(nextZoom);
    if (viewport) {
      viewport.scrollLeft = 0;
      viewport.scrollTop = 0;
    }
  }, [isImagePreview]);

  const applyZoomAtViewportPoint = useCallback(
    (nextZoom: number, clientX: number, clientY: number) => {
      const viewport = imageViewportRef.current;
      if (!viewport || !isImagePreview) return;

      const boundedZoom = clampZoom(nextZoom);
      const currentZoom = zoomRef.current;
      if (boundedZoom === currentZoom) return;

      const viewportRect = viewport.getBoundingClientRect();
      const pointX = clientX - viewportRect.left;
      const pointY = clientY - viewportRect.top;
      const scaleRatio = boundedZoom / currentZoom;
      const targetScrollLeft = Math.max(
        0,
        (viewport.scrollLeft + pointX) * scaleRatio - pointX
      );
      const targetScrollTop = Math.max(
        0,
        (viewport.scrollTop + pointY) * scaleRatio - pointY
      );

      zoomRef.current = boundedZoom;
      setZoom(boundedZoom);
      window.requestAnimationFrame(() => {
        if (sourceKeyRef.current !== activeKey || !imageViewportRef.current)
          return;
        imageViewportRef.current.scrollLeft = targetScrollLeft;
        imageViewportRef.current.scrollTop = targetScrollTop;
      });
    },
    [activeKey, isImagePreview]
  );

  const handleImageViewportRef = useCallback((node: HTMLDivElement | null) => {
    imageViewportRef.current = node;
    setImageViewportElement(node);
  }, []);

  const handleImageLoad = useCallback(
    (naturalWidth: number, naturalHeight: number) => {
      if (!isImagePreview) return;
      if (!naturalWidth || !naturalHeight) return;

      const fitToViewport = (attempt = 0) => {
        const viewport = imageViewportRef.current;
        if (sourceKeyRef.current !== activeKey || !viewport) return;

        // Image canvas has 12px padding on both sides.
        const viewportWidth = Math.max(0, viewport.clientWidth - 24);
        const viewportHeight = Math.max(0, viewport.clientHeight - 24);

        // During fast src swaps the image can load before viewport dimensions settle.
        if ((viewportWidth < 32 || viewportHeight < 32) && attempt < 10) {
          window.requestAnimationFrame(() => fitToViewport(attempt + 1));
          return;
        }

        const safeViewportWidth = Math.max(1, viewportWidth);
        const safeViewportHeight = Math.max(1, viewportHeight);
        const heightAtZoomOne =
          safeViewportWidth * (naturalHeight / naturalWidth);
        const fitZoom = clampZoom(
          Math.min(1, safeViewportHeight / heightAtZoomOne)
        );

        initialImageZoomRef.current = fitZoom;
        zoomRef.current = fitZoom;
        setZoom(fitZoom);
        viewport.scrollLeft = 0;
        viewport.scrollTop = 0;
      };

      fitToViewport();
    },
    [activeKey, isImagePreview]
  );

  const handleImageDoubleClick = useCallback(
    (event: ReactMouseEvent<HTMLImageElement>) => {
      event.preventDefault();
      applyZoomAtViewportPoint(
        zoomRef.current + ZOOM_STEP,
        event.clientX,
        event.clientY
      );
    },
    [applyZoomAtViewportPoint]
  );

  useEffect(() => {
    if (!isImagePreview) {
      initialImageZoomRef.current = 1;
      resetZoom();
    }
    if (imageViewportRef.current) {
      imageViewportRef.current.scrollLeft = 0;
      imageViewportRef.current.scrollTop = 0;
    }
  }, [activeKey, isImagePreview, resetZoom]);

  useEffect(() => {
    const viewport = imageViewportElement;
    if (!viewport || !isImagePreview) return;

    const handleViewportWheel = (event: WheelEvent) => {
      if (!event.ctrlKey) return;
      event.preventDefault();
      event.stopPropagation();
      const direction = event.deltaY < 0 ? 1 : -1;
      applyZoomAtViewportPoint(
        zoomRef.current + direction * ZOOM_STEP,
        event.clientX,
        event.clientY
      );
    };

    viewport.addEventListener('wheel', handleViewportWheel, {
      passive: false,
    });

    return () => {
      viewport.removeEventListener('wheel', handleViewportWheel);
    };
  }, [applyZoomAtViewportPoint, imageViewportElement, isImagePreview]);

  return {
    zoom,
    zoomIn: () => adjustZoom(ZOOM_STEP),
    zoomOut: () => adjustZoom(-ZOOM_STEP),
    resetZoom,
    handleImageDoubleClick,
    handleImageViewportRef,
    handleImageLoad,
  };
};
